import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeSelector from './ThemeSelector';
import ImpersonationBanner from './ImpersonationBanner';
import EmailVerificationBanner from './EmailVerificationBanner';
import SidebarNav from './SidebarNav';
import { PwaShellBars } from '../mambaPlatform/components/PwaShellBars';
import { InstallAppButton } from '../mambaPlatform/components/InstallAppButton';

const Layout: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-[100dvh] bg-app">
      <ImpersonationBanner />
      <EmailVerificationBanner />
      <PwaShellBars />
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-[var(--color-bg-overlay)] lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — token: sidebar-bg */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-sidebar-bg shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex h-full flex-col">
          {/* Logo - LaunchPoint branding */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-[color:var(--color-sidebar-border)]">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">LP</span>
              </div>
              <h1 className="ml-3 text-xl font-semibold text-sidebar-fg">
                LaunchPoint
              </h1>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded text-sidebar-fg-muted hover:text-sidebar-fg hover:bg-sidebar-hover"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <SidebarNav role={user?.role} onNavigate={() => setSidebarOpen(false)} />

          {/* User info */}
          <div className="border-t border-[color:var(--color-sidebar-border)] p-4">
            <div className="flex items-center mb-3">
              <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-medium text-white">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </span>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-sidebar-fg truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-sidebar-fg-muted truncate">{user?.tenant?.name}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center px-3 py-2 text-sm text-sidebar-fg-muted hover:text-sidebar-fg hover:bg-sidebar-hover rounded transition-colors"
            >
              <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Top bar - Mamba style */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex min-h-14 items-center justify-between px-3 sm:px-6 pt-[env(safe-area-inset-top)] bg-header-bg border-b border-header-border shadow-sm">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded text-fg-muted hover:text-fg hover:bg-surface-muted"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex items-center gap-2 sm:gap-3 ml-auto">
            <button className="hidden sm:inline-flex p-2 rounded text-fg-muted hover:text-fg hover:bg-surface-muted" aria-label="Notifications">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </button>
            <ThemeSelector />
            <div className="hidden sm:block">
              <InstallAppButton />
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-fg">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-fg-muted">{user?.role}</p>
              </div>
              <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center">
                <span className="text-sm font-medium text-white">{user?.first_name?.[0]}{user?.last_name?.[0]}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-6 sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]">
          <div className="mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
